import { useEffect, useRef, useState } from "react";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import CircleGeom from "ol/geom/Circle";
import { fromLonLat } from "ol/proj";
import { Style, Icon, Stroke, Fill } from "ol/style";
import { Button, Typography } from "antd";
import { AimOutlined } from "@ant-design/icons";
import { XYZ } from "ol/source";
import { boundingExtent } from "ol/extent";
import { BASE as OUTLET_POINT } from "../utils/location";
import { defaults as defaultControls } from "ol/control";

export default function DeliveryTrackLocation({ order }) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const userFeatureRef = useRef(null);
  const routeFeatureRef = useRef(null);

  const [distance, setDistance] = useState(0);
  const [points, setpoints] = useState({
    start_lat: OUTLET_POINT.lat,
    start_lng: OUTLET_POINT.lng,
    end_lat: order.deliveryLocation.lat,
    end_lng: order.deliveryLocation.lng
  });

  /* ---------------- INIT MAP ---------------- */
  useEffect(() => {
    var { end_lat, end_lng, start_lat, start_lng } = points

    const userSource = new VectorSource();
    const routeSource = new VectorSource();

    const map = new Map({
      target: mapDivRef.current,
      controls: defaultControls({ zoom: !["DELIVERED", "CANCELLED"].includes(order.adminStatus) }),
      layers: [
        new TileLayer({
          source: new XYZ({
            url: "https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
          }),
        }),
        new VectorLayer({ source: routeSource }),
        new VectorLayer({ source: userSource }),
      ],
      view: new View({
        center: fromLonLat([end_lng, end_lat]),
        zoom: 15,
      }),
    });

    map.getInteractions().forEach((interaction) => {
      interaction.setActive(!["DELIVERED", "CANCELLED"].includes(order.adminStatus));
    });

    mapRef.current = map;

    /* ---------- OUTLET ---------- */
    const outlet = new Feature(
      new Point(fromLonLat([OUTLET_POINT.lng, OUTLET_POINT.lat]))
    );
    outlet.setStyle(
      new Style({
        image: new Icon({
          src: "/image/logo_pin.png",
          scale: 0.5,
        }),
      })
    );
    userSource.addFeature(outlet);


    /* ---------- USER ICON ---------- */
    const userFeature = new Feature(
      new Point(fromLonLat([order?.partnerLocation?.lng, order?.partnerLocation?.lat]))
    );
    userFeature.setStyle(
      new Style({
        image: new Icon({
          src: "/image/bike-delivery.png",
          scale: 0.25,
          rotateWithView: true,
        }),
      })
    );

    if (!["DELIVERED", "CANCELLED"].includes(order.adminStatus)) {
      userSource.addFeature(userFeature);
    }

    /* ---------- dlivery ICON ---------- */
    const deliveryFeature = new Feature(
      new Point(fromLonLat([order.deliveryLocation.lng, order.deliveryLocation.lat]))
    );
    deliveryFeature.setStyle(
      new Style({
        image: new Icon({
          src: "/image/user_pin.png",
          scale: 0.15,
          rotateWithView: true,
        }),
      })
    );
    userSource.addFeature(deliveryFeature);


    userFeatureRef.current = ["DELIVERED", "CANCELLED"].includes(order.adminStatus) ? userFeature : deliveryFeature;

    /* ---------- ROUTE ---------- */
    const routeFeature = new Feature(new LineString([]));
    routeFeature.setStyle(
      new Style({
        stroke: new Stroke({ color: "#1677ff", width: 5 }),
      })
    );
    routeSource.addFeature(routeFeature);
    routeFeatureRef.current = routeFeature;

    return () => map.setTarget(null);
  }, [order]);

  /* ---------------- FIT USER + OUTLET (ONCE) ---------------- */
  const fitUserAndOutlet = _ => {
    const view = mapRef.current.getView();

    const startCoord = fromLonLat([points.start_lng, points.start_lat]);
    const endCoord = fromLonLat([points.end_lng, points.end_lat]);

    const extent = boundingExtent([endCoord, startCoord]);

    view.fit(extent, {
      padding: [80, 80, 80, 80],
      maxZoom: 16,
      duration: 600,
    });
  };


  /* ---------------- FETCH ROUTE ---------------- */
  const fetchRoute = async _ => {
    const url = `https://router.project-osrm.org/route/v1/driving/${points.start_lng},${points.start_lat};${points.end_lng},${points.end_lat}?overview=full&geometries=geojson`;

    const res = await fetch(url);
    const data = await res.json();
    if (!data.routes?.length) return;

    const coords = data.routes[0].geometry.coordinates.map((c) =>
      fromLonLat(c)
    );

    routeFeatureRef.current.setGeometry(new LineString(coords));
    setDistance((data.routes[0].distance / 1000).toFixed(2));
  };

  /* ---------------- LIVE GPS ---------------- */
  useEffect(() => {
    if (order.deliveryStatus === "OUT_FOR_DELIVERY") {
      setpoints({
        ...points,
        start_lat: order.partnerLocation?.lat,
        start_lng: order.partnerLocation?.lng,
        end_lat: order.deliveryLocation?.lat,
        end_lng: order.deliveryLocation?.lng
      })
    } 
    
    if (order.deliveryStatus === "DELIVERED") {
      setpoints({
        ...points,
        start_lat: OUTLET_POINT.lat,
        start_lng: OUTLET_POINT.lng
      })
    }
    const pos = fromLonLat([points.end_lng, points.end_lat]);
    userFeatureRef.current.getGeometry().setCoordinates(pos);
    fitUserAndOutlet();
    fetchRoute();

  }, [order]);


  return (
    <>
      <div style={{ position: "relative" }}>
        <div ref={mapDivRef} style={{ height: 400, width: "100%" }} />

        {!["DELIVERED", "CANCELLED"].includes(order.adminStatus) && <Button
          type="primary"
          shape="circle"
          icon={<AimOutlined />}
          onClick={fitUserAndOutlet}
          style={{
            position: "absolute",
            bottom: 20,
            right: 20,
            zIndex: 1000,
          }}
        />}
      </div>

      {/* <br /> */}

      {/* <Text>Mode: {mode}</Text><br />
      <Text>Distance: {distance} km</Text><br />
      <Text>Estimated Time: {formatETA(eta)}</Text> */}
    </>
  );
}
